// 会员等级相关常量和工具函数

import type { MembershipLevel, MembershipLevelType } from '@/types/personal-center'

/**
 * 会员等级定义
 */
export const MEMBERSHIP_LEVELS: Record<MembershipLevelType, MembershipLevel> = {
  P1: {
    level: 'P1',
    title: 'Bronze Member',
    benefits: [
      'Basic project access',
      'Standard customer support',
      'Monthly reports'
    ],
    requirements: {
      min_investment: 0,
      min_carbon_credits: 0
    },
    badge_color: '#CD7F32',
    badge_gradient: ['#CD7F32', '#B8860B']
  },
  P2: {
    level: 'P2',
    title: 'Silver Member',
    benefits: [
      'Priority project access',
      'Enhanced customer support',
      'Weekly reports',
      'Investment insights'
    ],
    requirements: {
      min_investment: 10000,
      min_carbon_credits: 50
    },
    badge_color: '#C0C0C0',
    badge_gradient: ['#C0C0C0', '#A8A8A8']
  },
  P3: {
    level: 'P3',
    title: 'Gold Member',
    benefits: [
      'Exclusive project access',
      'Premium customer support',
      'Daily reports',
      'Advanced analytics',
      'Personal advisor'
    ],
    requirements: {
      min_investment: 50000,
      min_carbon_credits: 250
    },
    badge_color: '#FFD700',
    badge_gradient: ['#FFD700', '#FFA500']
  },
  P4: {
    level: 'P4',
    title: 'Platinum Member',
    benefits: [
      'VIP project access',
      'Dedicated support team',
      'Real-time reports',
      'Custom analytics',
      'Investment concierge',
      'Early access to new projects'
    ],
    requirements: {
      min_investment: 100000,
      min_carbon_credits: 500
    },
    badge_color: '#E5E4E2',
    badge_gradient: ['#E5E4E2', '#D3D3D3']
  },
  P5: {
    level: 'P5',
    title: 'Diamond Member',
    benefits: [
      'Ultimate project access',
      'White-glove service',
      'Custom reporting',
      'Exclusive events',
      'Direct CEO access',
      'Custom investment strategies',
      'Priority withdrawals'
    ],
    requirements: {
      min_investment: 500000,
      min_carbon_credits: 2500
    },
    badge_color: '#B9F2FF',
    badge_gradient: ['#B9F2FF', '#87CEEB']
  }
}

/**
 * 获取会员等级信息
 */
export function getMembershipLevelInfo(level: MembershipLevelType): MembershipLevel {
  return MEMBERSHIP_LEVELS[level]
}

/**
 * 获取下一个会员等级
 */
export function getNextMembershipLevel(currentLevel: MembershipLevelType): MembershipLevel | null {
  const levels: MembershipLevelType[] = ['P1', 'P2', 'P3', 'P4', 'P5']
  const currentIndex = levels.indexOf(currentLevel)
  
  if (currentIndex === -1 || currentIndex === levels.length - 1) {
    return null
  }
  
  return MEMBERSHIP_LEVELS[levels[currentIndex + 1]]
}

/**
 * 计算升级进度
 */
export function calculateUpgradeProgress(
  currentLevel: MembershipLevelType,
  currentInvestment: number,
  currentCarbonCredits: number
): number {
  const nextLevel = getNextMembershipLevel(currentLevel)
  
  if (!nextLevel) {
    return 100 // 已达到最高等级
  }
  
  const investmentProgress = Math.min(
    (currentInvestment / nextLevel.requirements.min_investment) * 100,
    100
  )
  
  const carbonCreditsProgress = Math.min(
    (currentCarbonCredits / nextLevel.requirements.min_carbon_credits) * 100,
    100
  )
  
  // 取两个条件的最小值作为整体进度
  return Math.min(investmentProgress, carbonCreditsProgress)
}

/**
 * 检查是否满足升级条件
 */
export function canUpgradeToNextLevel(
  currentLevel: MembershipLevelType,
  currentInvestment: number,
  currentCarbonCredits: number
): boolean {
  const nextLevel = getNextMembershipLevel(currentLevel)
  
  if (!nextLevel) {
    return false
  }
  
  return (
    currentInvestment >= nextLevel.requirements.min_investment &&
    currentCarbonCredits >= nextLevel.requirements.min_carbon_credits
  )
}

/**
 * 获取升级所需的投资金额
 */
export function getInvestmentNeededForUpgrade(
  currentLevel: MembershipLevelType,
  currentInvestment: number
): number {
  const nextLevel = getNextMembershipLevel(currentLevel)
  
  if (!nextLevel) {
    return 0
  }
  
  return Math.max(0, nextLevel.requirements.min_investment - currentInvestment)
}

/**
 * 获取升级所需的碳信用数量
 */
export function getCarbonCreditsNeededForUpgrade(
  currentLevel: MembershipLevelType,
  currentCarbonCredits: number
): number {
  const nextLevel = getNextMembershipLevel(currentLevel)
  
  if (!nextLevel) {
    return 0
  }
  
  return Math.max(0, nextLevel.requirements.min_carbon_credits - currentCarbonCredits)
}